import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { TruckIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner, ErrorMessage } from '../../../components/common';
import ReturnItemInfo from '../../../components/actions/shared/ReturnItemInfo';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import Dropdown from '../../../components/ui/Dropdown';

import { 
  useGetFulfillmentGroupDetailsQuery 
} from '../../../redux/apis/endpoints/orderApi';
import { useSubmitGroupReturnRequestMutation } from '../../../redux/apis/endpoints/requestApi';
import { orderItemApi } from '../../../redux/apis/endpoints/orderItemApi';
import { fulfillmentGroupApi } from '../../../redux/apis/endpoints/fulfillmentGroupApi';

// Map reasons to the format expected by Dropdown
const RETURN_REASONS_OPTIONS = [
  { value: '', label: 'Select a reason' },
  { value: 'defective', label: 'Item is defective' },
  { value: 'wrong_size', label: 'Wrong size' },
  { value: 'not_as_described', label: 'Not as described' },
  { value: 'changed_mind', label: 'Changed my mind' },
  { value: 'damaged_shipping', label: 'Damaged during shipping' },
  { value: 'better_price', label: 'Found better price elsewhere' },
  { value: 'late_delivery', label: 'Delivery was too late' },
  { value: 'other', label: 'Other reason' }
];

const RequestReturnPage = () => {
  const { fulfillmentGroupId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [returnReason, setReturnReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  
  // RTK Query hooks
  const { 
    data: entity,
    isLoading: isLoadingFG,
    error: loadingError
  } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupId);
  
  const [submitGroupReturnRequest, { isLoading: isSubmitting }] = useSubmitGroupReturnRequestMutation();
  
  // Filter eligible items
  const eligibleItems = React.useMemo(() => {
    if (!entity?.orderItems) return [];
    
    return entity.orderItems.filter(item => {
      // Only include items that can be returned
      return ['delivered', 'pickup_completed_by_customer', 'in_use', 'completed'].includes(item.status);
    });
  }, [entity]);
  
  // Initialize selected items state
  useEffect(() => {
    if (eligibleItems.length > 0) {
      setSelectedItems(new Set());
    }
  }, [eligibleItems]);
  
  // Count selected items
  const selectedItemCount = selectedItems.size;
  
  // Handle item selection toggle
  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };
  
  // Handle select all
  const handleSelectAll = () => {
    if (selectedItems.size === eligibleItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(eligibleItems.map(item => item.id)));
    }
  };
  
  // Handle reason change
  const handleReasonChange = (selectedReasonValue) => {
    setReturnReason(selectedReasonValue);
    if (selectedReasonValue !== 'other') {
      setOtherReason('');
    }
  };
  
  // Submit return request
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (selectedItemCount === 0) {
      toast.error('Please select at least one item to return');
      return;
    }
    
    if (!returnReason) {
      toast.error('Please select a reason for your return');
      return;
    }
    
    if (returnReason === 'other' && !otherReason.trim()) {
      toast.error('Please specify a reason for your return');
      return;
    }
    
    // Get selected item IDs
    const itemIds = Array.from(selectedItems);
    
    try {
      console.log('🔄 Creating group return request:', {
        selectedItemIds: itemIds,
        returnReason,
        otherReason,
        additionalComments,
        fulfillmentGroup: entity
      });
      
      // Prepare payload
      const payload = {
        fulfillmentGroupId,
        orderItemIds: itemIds,
        reason: returnReason === 'other' ? otherReason.trim() : returnReason,
        comments: additionalComments
      };
      
      // Optimistically update fulfillment group status
      dispatch(
        fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
          if (draft) {
            draft.status = 'return_requested';
            // Update selected order items status
            if (draft.orderItems) {
              draft.orderItems.forEach(item => {
                if (itemIds.includes(item.id)) {
                  item.status = 'return_requested_by_customer';
                }
              });
            }
          }
        })
      );

      // Optimistically update individual order item queries
      itemIds.forEach(itemId => {
        dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', itemId, (draft) => {
            if (draft) {
              draft.status = 'return_requested_by_customer';
            }
          })
        );
      });
      
      // Submit return request
      const results = await submitGroupReturnRequest(payload).unwrap();
      console.log('✅ Group return request created successfully:', results);
      
      // Enhanced cache invalidation to trigger immediate UI updates
      const tags = [
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'FulfillmentGroup', id: fulfillmentGroupId }
      ];
      
      // Add specific order item tags for the updated items
      itemIds.forEach(itemId => {
        tags.push({ type: 'OrderItem', id: itemId });
      });
      
      console.log('🔄 Invalidating cache tags for return requests:', tags);
      dispatch(orderItemApi.util.invalidateTags(tags));
      
      // Show success message
      toast.success(`Return request submitted for ${itemIds.length} item${itemIds.length > 1 ? 's' : ''}`);
      
      // Redirect to fulfillment group page
      history.push(`/dashboard/transactions/fg/${fulfillmentGroupId}`);
    } catch (error) {
      console.error('❌ Failed to create return request:', {
        error,
        errorMessage: error?.message,
        errorData: error?.data,
        status: error?.status
      });
      
      // Revert optimistic updates on error
      const itemIds = Array.from(selectedItems);
      
      dispatch(
        fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
          if (draft) {
            // Revert to original status - assume it was delivered
            draft.status = 'delivery_completed_to_customer';
            if (draft.orderItems) {
              draft.orderItems.forEach(item => {
                if (itemIds.includes(item.id)) {
                  item.status = 'delivered'; // Revert to delivered status
                }
              });
            }
          }
        })
      );

      itemIds.forEach(itemId => {
        dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', itemId, (draft) => {
            if (draft) {
              draft.status = 'delivered'; // Revert to delivered status
            }
          })
        );
      });
      
      toast.error(error.data?.message || error.message || 'Failed to submit return request');
    }
  };
  
  // Show loading state
  if (isLoadingFG) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  // Show error state
  if (loadingError) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <ErrorMessage message={loadingError.message || 'Error loading order data'} />
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </div>
    );
  }
  
  // Show no eligible items message
  if (eligibleItems.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <h2 className="text-xl font-medium text-gray-900 mb-4">No Eligible Items</h2>
          <p className="text-gray-600 mb-4">
            There are no items eligible for return in this order.
          </p>
          <Button
            variant="secondary"
            onClick={() => history.goBack()}
          >
            Back to Order
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center mb-6">
        <ArrowUturnLeftIcon className="h-10 w-10 text-brand-teal mr-4" />
        <div>
          <h1 className="text-2xl font-medium text-gray-900">Request Return</h1>
          <p className="text-sm text-gray-600 mt-1">
            Select the items you want to return
          </p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Items Selection */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
            {/* <h3 className="font-medium text-gray-900">Select Items to Return</h3> */}
            <button
              type="button"
              className="text-l text-brand-teal hover:text-brand-teal-dark focus:outline-none"
              onClick={handleSelectAll}
            >
              {selectedItems.size === eligibleItems.length 
                ? 'Unselect All' 
                : 'Select All'}
            </button>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {eligibleItems.map(item => (
              <div key={item.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`item-${item.id}`}
                    checked={selectedItems.has(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                    className="h-5 w-5 text-brand-teal focus:ring-brand-teal border-gray-300 rounded"
                  />
                  <label 
                    htmlFor={`item-${item.id}`}
                    className="ml-3 block w-full cursor-pointer"
                  >
                    <ReturnItemInfo item={item} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Return Reason */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <Dropdown
            title="Reason for Return"
            options={RETURN_REASONS_OPTIONS}
            value={returnReason}
            onChange={handleReasonChange}
            required
            placeholder="Select a reason"
          />
          
          {returnReason === 'other' && (
            <div className="mt-4">
              <FormInput
                label="Please specify *"
                type="text"
                name="otherReason"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
          )}
          
          <div className="mt-4">
            <FormInput
              label="Additional Comments (Optional)"
              as="textarea"
              name="comments"
              rows="4"
              placeholder="Please provide any additional details about your return request..."
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
        </div>
        
        {/* Return Process Info */}
        {selectedItemCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              Return Process:
            </h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Return requests require merchant approval.</li>
              <li>If <span className="font-semibold">approved</span>, you pay a <span className="font-semibold">usage fee</span> for time used <em>plus</em> return shipping.</li>
              <li>The merchant may <span className="font-semibold">decline</span> your request.</li>
             <li>Refund will be processed after items are received and inspected.</li>
            </ul>
          </div>
        )}
        
        {/* Submit Button */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => history.goBack()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="primary"
            disabled={selectedItemCount === 0 || !returnReason || isSubmitting}
            isLoading={isSubmitting}
          >
            <ArrowUturnLeftIcon className="h-4 w-4 mr-2" />
            Request Return for {selectedItemCount} Item{selectedItemCount !== 1 ? 's' : ''}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RequestReturnPage; 